import { useState, useEffect, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabase'
import { format, formatDistanceToNow } from 'date-fns'
import { createDogNameMap } from '@/lib/dogNames'
import { LiveIndicator } from '@/components/dashboard/LiveIndicator'
import { ConversionBadge } from '@/components/dashboard/ConversionBadge'
import type { Session } from '@/types/database'
import type { VisitorStatus } from '@/types/dashboard'

const LIVE_THRESHOLD_SECONDS = 20
const IDLE_THRESHOLD_SECONDS = 30

interface SessionWithConversion extends Session {
  isConverted: boolean
  status: VisitorStatus
}

export default function SessionsPage() {
  const { site } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [sessions, setSessions] = useState<SessionWithConversion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Get visitor filter from URL params
  const visitorHashFilter = searchParams.get('visitor') || ''

  const [filters, setFilters] = useState({
    deviceType: '' as 'desktop' | 'mobile' | 'tablet' | '',
    countryCode: '',
    converted: '' as 'all' | 'yes' | 'no' | '',
  })
  const limit = 20

  useEffect(() => {
    if (site) {
      fetchSessions()
    }
  }, [site, page, filters, visitorHashFilter])

  const fetchSessions = async () => {
    if (!site) return
    setIsLoading(true)

    try {
      // Build query with filters
      let countQuery = supabase
        .from('sessions')
        .select('*', { count: 'exact', head: true })
        .eq('site_id', site.id)

      let dataQuery = supabase
        .from('sessions')
        .select('*')
        .eq('site_id', site.id)
        .order('started_at', { ascending: false })

      if (filters.deviceType) {
        countQuery = countQuery.eq('device_type', filters.deviceType)
        dataQuery = dataQuery.eq('device_type', filters.deviceType)
      }

      if (filters.countryCode) {
        countQuery = countQuery.ilike('country_code', `%${filters.countryCode}%`)
        dataQuery = dataQuery.ilike('country_code', `%${filters.countryCode}%`)
      }

      // Filter by visitor hash (from URL param)
      if (visitorHashFilter) {
        countQuery = countQuery.eq('visitor_hash', visitorHashFilter)
        dataQuery = dataQuery.eq('visitor_hash', visitorHashFilter)
      }

      // Get total count
      const { count } = await countQuery
      setTotalPages(Math.ceil((count || 0) / limit))

      // Get sessions
      const { data } = await dataQuery.range((page - 1) * limit, page * limit - 1) as { data: Session[] | null }
      const sessionsData = data || []

      // Fetch conversion data
      const sessionIds = sessionsData.map(s => s.id)
      const { data: conversionEvents } = await supabase
        .from('events')
        .select('session_id')
        .eq('site_id', site.id)
        .eq('event_type', 'form_submit')
        .in('session_id', sessionIds) as { data: { session_id: string }[] | null }

      const convertedSessionIds = new Set(conversionEvents?.map(e => e.session_id) || [])
      const now = new Date()
      const liveThreshold = new Date(now.getTime() - LIVE_THRESHOLD_SECONDS * 1000)

      let enrichedSessions: SessionWithConversion[] = sessionsData.map(s => {
        const hasRecentHeartbeat = new Date(s.last_seen_at) >= liveThreshold
        const idleSeconds = s.idle_seconds || 0
        let status: VisitorStatus = 'offline'
        if (hasRecentHeartbeat) {
          status = idleSeconds >= IDLE_THRESHOLD_SECONDS ? 'inactive' : 'live'
        }
        return {
          ...s,
          isConverted: convertedSessionIds.has(s.id),
          status,
        }
      })

      // Filter by conversion status
      if (filters.converted === 'yes') {
        enrichedSessions = enrichedSessions.filter(s => s.isConverted)
      } else if (filters.converted === 'no') {
        enrichedSessions = enrichedSessions.filter(s => !s.isConverted)
      }

      setSessions(enrichedSessions)
    } catch (error) {
      console.error('Failed to fetch sessions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Create dog name map for current sessions (include visitorHashFilter for the banner)
  const dogNameMap = useMemo(() => {
    const hashes = sessions.map(s => s.visitor_hash)
    // Include the filter hash if it's set, so we can show the name in the banner
    if (visitorHashFilter && !hashes.includes(visitorHashFilter)) {
      hashes.push(visitorHashFilter)
    }
    return createDogNameMap(hashes)
  }, [sessions, visitorHashFilter])

  const getDeviceIcon = (type: string | null) => {
    switch (type) {
      case 'mobile':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        )
      case 'tablet':
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        )
      default:
        return (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
    }
  }

  const clearFilters = () => {
    setFilters({ deviceType: '', countryCode: '', converted: '' })
    // Also clear visitor filter from URL
    if (visitorHashFilter) {
      setSearchParams({})
    }
    setPage(1)
  }

  const clearVisitorFilter = () => {
    setSearchParams({})
    setPage(1)
  }

  const hasFilters = filters.deviceType || filters.countryCode || filters.converted || visitorHashFilter

  if (!site) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No site configured</h2>
        <p className="text-gray-600 mb-4">Add your website to start tracking analytics.</p>
        <Link to="/settings" className="btn-primary">Add your site</Link>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Sessions</h1>
        <p className="text-gray-600">View visitor sessions and their journeys</p>
      </div>

      {/* Visitor filter banner */}
      {visitorHashFilter && (
        <div className="card mb-4 bg-primary-50 border-primary-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-primary-600">👤</span>
              <span className="text-sm text-primary-700">
                Showing sessions for visitor: <span className="font-medium">{dogNameMap.get(visitorHashFilter) || visitorHashFilter.slice(0, 8)}</span>
              </span>
            </div>
            <button
              onClick={clearVisitorFilter}
              className="text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              Clear filter
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="label">Device Type</label>
            <select
              value={filters.deviceType}
              onChange={(e) => {
                setFilters({ ...filters, deviceType: e.target.value as typeof filters.deviceType })
                setPage(1)
              }}
              className="input"
            >
              <option value="">All devices</option>
              <option value="desktop">Desktop</option>
              <option value="mobile">Mobile</option>
              <option value="tablet">Tablet</option>
            </select>
          </div>
          <div>
            <label className="label">Country</label>
            <input
              type="text"
              value={filters.countryCode}
              onChange={(e) => {
                setFilters({ ...filters, countryCode: e.target.value.toUpperCase() })
                setPage(1)
              }}
              placeholder="US, GB..."
              className="input w-24"
              maxLength={2}
            />
          </div>
          <div>
            <label className="label">Converted</label>
            <select
              value={filters.converted}
              onChange={(e) => {
                setFilters({ ...filters, converted: e.target.value as typeof filters.converted })
                setPage(1)
              }}
              className="input"
            >
              <option value="">All</option>
              <option value="yes">Converted</option>
              <option value="no">Not converted</option>
            </select>
          </div>
          {hasFilters && (
            <div className="flex items-end">
              <button onClick={clearFilters} className="btn-secondary">
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600">No sessions found</p>
          <p className="text-sm text-gray-500 mt-1">
            {hasFilters ? 'Try adjusting your filters' : 'Sessions will appear here once visitors start browsing your site'}
          </p>
        </div>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b bg-gray-50">
                  <th className="px-4 py-3 font-medium w-12">Live</th>
                  <th className="px-4 py-3 font-medium">Visitor</th>
                  <th className="px-4 py-3 font-medium">Device</th>
                  <th className="px-4 py-3 font-medium">Entry Page</th>
                  <th className="px-4 py-3 font-medium">Pages</th>
                  <th className="px-4 py-3 font-medium">Converted</th>
                  <th className="px-4 py-3 font-medium">Started</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <LiveIndicator status={session.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {session.country_code && (
                          <span className="text-sm">{getFlagEmoji(session.country_code)}</span>
                        )}
                        <span className="text-sm font-medium text-gray-900">
                          {dogNameMap.get(session.visitor_hash) || session.visitor_hash.slice(0, 8)}
                        </span>
                        {session.has_rage_clicks && (
                          <span className="badge-danger">Rage</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        {getDeviceIcon(session.device_type)}
                        <span className="text-sm">
                          {session.browser || 'Unknown'}
                          {session.os && ` / ${session.os}`}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900 truncate max-w-xs block">
                        {session.entry_page || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-900">{session.pageview_count}</span>
                    </td>
                    <td className="px-4 py-3">
                      <ConversionBadge isConverted={session.isConverted} />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600" title={format(new Date(session.started_at), 'PPpp')}>
                        {formatDistanceToNow(new Date(session.started_at), { addSuffix: true })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/sessions/${session.id}`}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function getFlagEmoji(countryCode: string): string {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}
